import { TEXT_LIMITS } from '@posilka/shared';
import { lazy, Suspense, useState } from 'react';
import { api } from '@posilka/client/api/client';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/Skeleton';
import {
  canOpenLocationSettings,
  getCurrentLocation,
  LocationError,
  openLocationSettings,
  type LocationErrorReason,
} from '@/lib/location';
import { haptic } from '@/lib/telegram';

export interface PickedPlace {
  address: string;
  lat: number;
  lng: number;
}

const YANDEX_KEY = import.meta.env.VITE_YANDEX_MAPS_API_KEY;

// Xarita alohida chunk (SPEC §15: asosiy chunk < 250 KB).
const YandexMapPicker = lazy(() => import('./YandexMapPicker'));

/**
 * Nuqta + manzil matni. Kalit bo'lsa — Yandex xarita (markaziy pin, reverse geocoder).
 * Kalit yo'q bo'lsa — matn + "Joylashuvimni olish" (GPS); koordinata default — shahar markazi.
 */
export function LocationPicker({
  value,
  onChange,
  center,
}: {
  value: PickedPlace;
  onChange: (p: PickedPlace) => void;
  center: { lat: number; lng: number };
}) {
  const { t, i18n } = useTranslation();
  const [locating, setLocating] = useState(false);
  const [resolving, setResolving] = useState(false);
  // Manzil matnini foydalanuvchi o'zi yozgan bo'lsa, avtomatik natija uni bosib ketmasin.
  const [autoFilled, setAutoFilled] = useState<string | null>(null);
  const [geoError, setGeoError] = useState<LocationErrorReason | null>(null);

  const locate = async () => {
    setLocating(true);
    setGeoError(null);
    try {
      const { lat, lng } = await getCurrentLocation();
      haptic.success();
      onChange({ ...value, lat, lng });
      setLocating(false);
      await fillAddress(lat, lng);
    } catch (e) {
      haptic.error();
      setGeoError(e instanceof LocationError ? e.reason : 'unavailable');
    } finally {
      setLocating(false);
    }
  };

  const fillAddress = async (lat: number, lng: number) => {
    const typedByUser = value.address.trim() !== '' && value.address !== autoFilled;
    if (typedByUser) return;
    setResolving(true);
    try {
      const { address } = await api<{ address: string | null }>(
        `/geo/reverse?lat=${lat}&lng=${lng}&lang=${i18n.language === 'ru' ? 'ru' : 'uz'}`,
      );
      if (address) {
        setAutoFilled(address);
        onChange({ address, lat, lng });
      }
    } catch {
      // Manzil topilmadi — foydalanuvchi o'zi yozadi, koordinata saqlangan.
    } finally {
      setResolving(false);
    }
  };

  const isDefault = value.lat === center.lat && value.lng === center.lng;

  return (
    <div className="space-y-2">
      {YANDEX_KEY ? (
        <Suspense fallback={<Skeleton className="h-56" />}>
          <YandexMapPicker
            apiKey={YANDEX_KEY}
            value={value}
            center={isDefault ? center : value}
            onChange={onChange}
          />
        </Suspense>
      ) : null}

      <textarea
        className="input min-h-20 resize-none"
        placeholder={t('location.addressPlaceholder')}
        value={value.address}
        maxLength={TEXT_LIMITS.address.max}
        onChange={(e) => onChange({ ...value, address: e.target.value })}
      />

      <div className="flex items-center justify-between gap-2 px-1">
        <button
          type="button"
          className="btn-secondary text-sm"
          onClick={locate}
          disabled={locating}
        >
          📍 {locating ? t('location.locating') : t('location.useMyLocation')}
        </button>
        <span className="text-xs text-hint">
          {resolving
            ? t('location.resolving')
            : isDefault
              ? t('location.cityCenter')
              : value.address && value.address === autoFilled
                ? `✓ ${t('location.detected')}`
                : `📍 ${value.lat.toFixed(4)}, ${value.lng.toFixed(4)}`}
        </span>
      </div>
      {geoError && (
        <div className="card px-3 py-2.5 text-sm">
          <p className="text-destructive">{t(`location.error.${geoError}`)}</p>
          {geoError === 'denied' && canOpenLocationSettings() && (
            <button type="button" className="mt-1 text-link" onClick={openLocationSettings}>
              {t('location.openSettings')} →
            </button>
          )}
        </div>
      )}
    </div>
  );
}
